// useNodeViewToolbarState.js

import { useCallback, useEffect, useMemo, useState } from 'react';

// Context
import { useCurrentLanguage } from 'context/LanguageContext';

// Hooks
import useNodeViewsState from 'node/hooks/useNodeViewsState';

// Utilities
import { getResolvedFields } from 'utilities/NodeType';
import { getViewRowIdentity, isCatchAllView } from 'utilities/ViewRows';
import buildCatchAllFacetFields from 'views/utils/buildCatchAllFacetFields';
import { buildActiveCatchAllFacets } from 'views/utils/applyCatchAllFacetFilter';
import {
  CATCH_ALL_BOARD_FIELD_MEMORY_STORAGE_KEY,
  CATCH_ALL_CALENDAR_FIELD_MEMORY_STORAGE_KEY,
  CATCH_ALL_FALLBACK_VIEW_TYPE,
  CATCH_ALL_PROPERTIES_MEMORY_STORAGE_KEY,
  CATCH_ALL_VIEW_TYPE_MEMORY_STORAGE_KEY,
  buildCatchAllBoardFields,
  buildCatchAllPropertyFields,
  catchAllViewTypeSupportsFieldChooser,
  getCatchAllPropertiesPreferenceKey,
  getCatchAllViewTypes,
  normalizeCatchAllViewType,
  readScopedPreferenceMap,
  resolveCatchAllBoardField,
  resolveCatchAllCalendarField,
  resolveCatchAllProperties,
  writeScopedPreferenceMap,
} from 'views/utils/organicViews';
import { isTemporalFieldType } from 'views/utils/temporalFieldFormat';

/**
 * useNodeViewToolbarState
 *
 * Manages saved views, catch-all display type, organic board field, and lens state for node surfaces.
 *
 * @param {object} params
 * @param {object|null} params.node
 * @param {string} params.activeTypeKey
 * @param {string|null} params.baseKey
 * @param {boolean} [params.freezeDisplay=false]
 * @param {boolean} [params.organicViewsEnabled=false]
 * @param {Array} [params.lensItems] - Current related items for Tags lens options.
 * @returns {object}
 */
export default function useNodeViewToolbarState({
  node,
  activeTypeKey,
  baseKey,
  freezeDisplay = false,
  organicViewsEnabled = false,
  lensItems = null,
}) {
  const language = useCurrentLanguage();

  const {
    views,
    activeViewId,
    setActiveViewId,
    activeView,
    isLoading,
  } = useNodeViewsState({
    node,
    activeTypeKey,
    baseKey,
  });

  const catchAllViewId = useMemo(() => {
    const match = views.find((view) => isCatchAllView(view));
    return getViewRowIdentity(match) || 'all';
  }, [views]);

  const filteredViews = useMemo(() => (
    views.filter((view) => !isCatchAllView(view))
  ), [views]);

  const [displayedViewsForBar, setDisplayedViewsForBar] = useState(filteredViews);
  const [displayedActiveViewIdForBar, setDisplayedActiveViewIdForBar] = useState(null);
  const [catchAllViewTypeByScope, setCatchAllViewTypeByScope] = useState(() => (
    readScopedPreferenceMap(CATCH_ALL_VIEW_TYPE_MEMORY_STORAGE_KEY)
  ));
  const [catchAllBoardFieldByScope, setCatchAllBoardFieldByScope] = useState(() => (
    readScopedPreferenceMap(CATCH_ALL_BOARD_FIELD_MEMORY_STORAGE_KEY)
  ));
  const [catchAllPropertiesByScopeType, setCatchAllPropertiesByScopeType] = useState(() => (
    readScopedPreferenceMap(CATCH_ALL_PROPERTIES_MEMORY_STORAGE_KEY)
  ));
  const [catchAllCalendarFieldByScope, setCatchAllCalendarFieldByScope] = useState(() => (
    readScopedPreferenceMap(CATCH_ALL_CALENDAR_FIELD_MEMORY_STORAGE_KEY)
  ));
  const [catchAllFacetSelections, setCatchAllFacetSelections] = useState({});

  const catchAllScopeKey = useMemo(() => (
    `${baseKey || ''}::${node?.type || ''}::${activeTypeKey || ''}`
  ), [baseKey, node?.type, activeTypeKey]);

  const resolvedProperties = useMemo(() => {
    const typeKey = String(activeTypeKey || '').trim();
    if (!typeKey || typeKey === '__HIGHLIGHT__') return [];
    return getResolvedFields(typeKey, { includeHidden: true, excludeSecured: true });
  }, [activeTypeKey]);

  const catchAllBoardFields = useMemo(() => (
    organicViewsEnabled ? buildCatchAllBoardFields(resolvedProperties) : []
  ), [organicViewsEnabled, resolvedProperties]);

  const catchAllPropertyFields = useMemo(() => (
    organicViewsEnabled ? buildCatchAllPropertyFields(resolvedProperties) : []
  ), [organicViewsEnabled, resolvedProperties]);

  const catchAllCalendarFields = useMemo(() => {
    if (!organicViewsEnabled) return [];
    return catchAllPropertyFields.filter((field) => isTemporalFieldType(field.type));
  }, [catchAllPropertyFields, organicViewsEnabled]);

  const allowBoard = catchAllBoardFields.length > 0;

  const catchAllViewTypes = useMemo(() => (
    getCatchAllViewTypes({
      organicEnabled: organicViewsEnabled,
      allowBoard,
    })
  ), [allowBoard, organicViewsEnabled]);

  const catchAllFacetFields = useMemo(() => (
    buildCatchAllFacetFields(resolvedProperties, language, {
      includeMultiselect: organicViewsEnabled,
      includeTags: organicViewsEnabled,
      items: lensItems,
    })
  ), [language, lensItems, organicViewsEnabled, resolvedProperties]);

  const activeCatchAllFacets = useMemo(() => (
    buildActiveCatchAllFacets(catchAllFacetSelections)
  ), [catchAllFacetSelections]);

  const activeResolvedViewId = String(activeView?.key || activeViewId || '').trim();
  const isCatchAllActive = useMemo(() => {
    if (isCatchAllView(activeView)) return true;

    const activeId = activeResolvedViewId.toLowerCase();
    const resolvedCatchAllId = String(catchAllViewId || '').trim().toLowerCase();
    if (!activeId) return true;

    return activeId === 'all' || (resolvedCatchAllId && activeId === resolvedCatchAllId);
  }, [activeResolvedViewId, activeView, catchAllViewId]);

  useEffect(() => {
    if (freezeDisplay || isLoading) return;
    setDisplayedViewsForBar(filteredViews);
    setDisplayedActiveViewIdForBar(isCatchAllActive ? null : activeViewId);
  }, [freezeDisplay, isLoading, filteredViews, isCatchAllActive, activeViewId]);

  useEffect(() => {
    setCatchAllFacetSelections({});
  }, [catchAllScopeKey]);

  useEffect(() => {
    const validOptionsByField = new Map(
      catchAllFacetFields.map((field) => [
        field.key,
        new Set(field.options.map((option) => String(option?.value || '').trim())),
      ]),
    );

    setCatchAllFacetSelections((prev) => {
      const previousSelections = prev && typeof prev === 'object' ? prev : {};
      let hasChanges = false;
      const nextSelections = {};

      Object.entries(previousSelections).forEach(([fieldKey, selectedValue]) => {
        const normalizedFieldKey = String(fieldKey || '').trim();
        const normalizedValue = String(selectedValue || '').trim();
        const validOptions = validOptionsByField.get(normalizedFieldKey);

        if (!normalizedFieldKey || !normalizedValue || !validOptions || !validOptions.has(normalizedValue)) {
          hasChanges = true;
          return;
        }

        nextSelections[normalizedFieldKey] = normalizedValue;
      });

      return hasChanges ? nextSelections : previousSelections;
    });
  }, [catchAllFacetFields]);

  const selectedCatchAllViewType = useMemo(() => {
    if (!isCatchAllActive) return null;

    const scopedType = catchAllViewTypeByScope[catchAllScopeKey];
    return normalizeCatchAllViewType(scopedType || activeView?.type || CATCH_ALL_FALLBACK_VIEW_TYPE, {
      organicEnabled: organicViewsEnabled,
      allowBoard,
    });
  }, [
    activeView?.type,
    allowBoard,
    catchAllScopeKey,
    catchAllViewTypeByScope,
    isCatchAllActive,
    organicViewsEnabled,
  ]);

  const selectedCatchAllBoardField = useMemo(() => {
    if (!organicViewsEnabled || !isCatchAllActive) return null;
    if (selectedCatchAllViewType !== 'BOARD') return null;

    return resolveCatchAllBoardField(
      catchAllBoardFieldByScope[catchAllScopeKey],
      catchAllBoardFields,
    ) || null;
  }, [
    catchAllBoardFieldByScope,
    catchAllBoardFields,
    catchAllScopeKey,
    isCatchAllActive,
    organicViewsEnabled,
    selectedCatchAllViewType,
  ]);

  const selectedCatchAllProperties = useMemo(() => {
    if (!organicViewsEnabled || !isCatchAllActive) return null;
    if (!catchAllViewTypeSupportsFieldChooser(selectedCatchAllViewType)) return null;
    if (selectedCatchAllViewType === 'CALENDAR') return null;

    const preferenceKey = getCatchAllPropertiesPreferenceKey(
      catchAllScopeKey,
      selectedCatchAllViewType,
    );
    return resolveCatchAllProperties(
      catchAllPropertiesByScopeType[preferenceKey],
      catchAllPropertyFields,
    );
  }, [
    catchAllPropertiesByScopeType,
    catchAllPropertyFields,
    catchAllScopeKey,
    isCatchAllActive,
    organicViewsEnabled,
    selectedCatchAllViewType,
  ]);

  const selectedCatchAllCalendarField = useMemo(() => {
    if (!organicViewsEnabled || !isCatchAllActive) return null;
    if (selectedCatchAllViewType !== 'CALENDAR') return null;
    if (catchAllCalendarFields.length < 2) {
      return resolveCatchAllCalendarField(null, catchAllCalendarFields) || null;
    }

    return resolveCatchAllCalendarField(
      catchAllCalendarFieldByScope[catchAllScopeKey],
      catchAllCalendarFields,
    ) || null;
  }, [
    catchAllCalendarFieldByScope,
    catchAllCalendarFields,
    catchAllScopeKey,
    isCatchAllActive,
    organicViewsEnabled,
    selectedCatchAllViewType,
  ]);

  const showFieldChooser = Boolean(
    organicViewsEnabled
    && isCatchAllActive
    && catchAllViewTypeSupportsFieldChooser(selectedCatchAllViewType)
    && (
      selectedCatchAllViewType === 'CALENDAR'
        ? catchAllCalendarFields.length > 0
        : catchAllPropertyFields.length > 0
    ),
  );

  const effectiveActiveView = useMemo(() => {
    if (!activeView || !isCatchAllView(activeView)) return activeView;

    const resolvedType = normalizeCatchAllViewType(
      selectedCatchAllViewType,
      {
        organicEnabled: organicViewsEnabled,
        allowBoard,
        fallback: normalizeCatchAllViewType(activeView?.type || CATCH_ALL_FALLBACK_VIEW_TYPE, {
          organicEnabled: organicViewsEnabled,
          allowBoard,
        }),
      },
    );

    const nextView = {
      ...activeView,
      type: resolvedType,
      properties: selectedCatchAllProperties,
    };

    if (resolvedType === 'BOARD' && selectedCatchAllBoardField) {
      nextView.boardField = selectedCatchAllBoardField;
      nextView.boardEnumOptions = {
        fieldKey: selectedCatchAllBoardField,
      };
    } else {
      nextView.boardField = null;
      nextView.boardEnumOptions = null;
    }

    if (resolvedType === 'CALENDAR' && selectedCatchAllCalendarField) {
      nextView.calendarField = selectedCatchAllCalendarField;
    } else if (resolvedType !== 'CALENDAR') {
      nextView.calendarField = null;
    }

    return nextView;
  }, [
    activeView,
    allowBoard,
    organicViewsEnabled,
    selectedCatchAllBoardField,
    selectedCatchAllCalendarField,
    selectedCatchAllProperties,
    selectedCatchAllViewType,
  ]);

  const handleCatchAllViewTypeSelect = useCallback((nextType) => {
    const normalizedType = normalizeCatchAllViewType(nextType, {
      organicEnabled: organicViewsEnabled,
      allowBoard,
    });

    setCatchAllFacetSelections((prev) => (
      Object.keys(prev || {}).length ? {} : prev
    ));

    setCatchAllViewTypeByScope((prev) => {
      const nextMap = {
        ...(prev || {}),
        [catchAllScopeKey]: normalizedType,
      };
      writeScopedPreferenceMap(CATCH_ALL_VIEW_TYPE_MEMORY_STORAGE_KEY, nextMap);
      return nextMap;
    });

    setActiveViewId(catchAllViewId || 'all');
  }, [
    allowBoard,
    catchAllScopeKey,
    catchAllViewId,
    organicViewsEnabled,
    setActiveViewId,
  ]);

  const handleCatchAllBoardFieldChange = useCallback((nextFieldKey) => {
    const resolvedFieldKey = resolveCatchAllBoardField(nextFieldKey, catchAllBoardFields);
    if (!resolvedFieldKey) return;

    setCatchAllBoardFieldByScope((prev) => {
      const nextMap = {
        ...(prev || {}),
        [catchAllScopeKey]: resolvedFieldKey,
      };
      writeScopedPreferenceMap(CATCH_ALL_BOARD_FIELD_MEMORY_STORAGE_KEY, nextMap);
      return nextMap;
    });

    // Drop a filter on the same field used for grouping.
    setCatchAllFacetSelections((prev) => {
      if (!prev?.[resolvedFieldKey]) return prev;
      const nextSelections = { ...prev };
      delete nextSelections[resolvedFieldKey];
      return nextSelections;
    });
  }, [catchAllBoardFields, catchAllScopeKey]);

  const handleCatchAllPropertiesChange = useCallback((nextProperties) => {
    const preferenceKey = getCatchAllPropertiesPreferenceKey(
      catchAllScopeKey,
      selectedCatchAllViewType || CATCH_ALL_FALLBACK_VIEW_TYPE,
    );
    const resolved = resolveCatchAllProperties(nextProperties, catchAllPropertyFields);

    setCatchAllPropertiesByScopeType((prev) => {
      const nextMap = {
        ...(prev || {}),
        [preferenceKey]: resolved,
      };
      writeScopedPreferenceMap(CATCH_ALL_PROPERTIES_MEMORY_STORAGE_KEY, nextMap);
      return nextMap;
    });
  }, [catchAllPropertyFields, catchAllScopeKey, selectedCatchAllViewType]);

  const handleCatchAllCalendarFieldChange = useCallback((nextFieldKey) => {
    const resolvedFieldKey = resolveCatchAllCalendarField(nextFieldKey, catchAllCalendarFields);
    if (!resolvedFieldKey) return;

    setCatchAllCalendarFieldByScope((prev) => {
      const nextMap = {
        ...(prev || {}),
        [catchAllScopeKey]: resolvedFieldKey,
      };
      writeScopedPreferenceMap(CATCH_ALL_CALENDAR_FIELD_MEMORY_STORAGE_KEY, nextMap);
      return nextMap;
    });
  }, [catchAllCalendarFields, catchAllScopeKey]);

  const handleCatchAllFacetSelectionChange = useCallback((fieldKey, nextValue) => {
    const normalizedFieldKey = String(fieldKey || '').trim();
    if (!normalizedFieldKey) return;

    const normalizedValue = String(nextValue || '').trim();

    setCatchAllFacetSelections((prev) => {
      const previousSelections = prev && typeof prev === 'object' ? prev : {};
      const currentValue = String(previousSelections[normalizedFieldKey] || '').trim();
      if (currentValue === normalizedValue) return previousSelections;

      const nextSelections = { ...previousSelections };
      if (normalizedValue) {
        nextSelections[normalizedFieldKey] = normalizedValue;
      } else {
        delete nextSelections[normalizedFieldKey];
      }

      return nextSelections;
    });
  }, []);

  return {
    activeCatchAllFacets,
    activeView,
    activeViewId,
    catchAllBoardFields,
    catchAllCalendarFields,
    catchAllFacetFields,
    catchAllFacetSelections,
    catchAllPropertyFields,
    catchAllViewId,
    catchAllViewTypes,
    displayedActiveViewIdForBar,
    displayedViewsForBar,
    effectiveActiveView,
    handleCatchAllBoardFieldChange,
    handleCatchAllCalendarFieldChange,
    handleCatchAllFacetSelectionChange,
    handleCatchAllPropertiesChange,
    handleCatchAllViewTypeSelect,
    isCatchAllActive,
    isLoading,
    organicViewsEnabled,
    selectedCatchAllBoardField,
    selectedCatchAllCalendarField,
    selectedCatchAllProperties,
    selectedCatchAllViewType,
    setActiveViewId,
    showFieldChooser,
    views,
  };
}
